import { useState } from 'react';
import './PrivacyPolicy.css';
import { EMAIL_PRIVACY, EMAIL_GDPR } from './config';
import { usersAPI } from './services/api';
import { useTranslation, Trans } from 'react-i18next';

function PrivacyPolicy({ currentUser }) {
  const { t } = useTranslation();
  const [exportLoading, setExportLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const downloadUserData = async (filename) => {
    if (!currentUser) return;
    setExportLoading(true);
    try {
      const data = await usersAPI.gdprExport(currentUser.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportMyData = () => downloadUserData('my-data');
  const handleDownloadJSON = () => downloadUserData('cactus-social-data');

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    try {
      setDeletingAccount(true);
      await usersAPI.gdprDelete(currentUser.id);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('Failed to delete account. Please try again.');
      setDeletingAccount(false);
    }
  };

  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <h1>{t('privacy.title')}</h1>
        <p className="last-updated">{t('privacy.lastUpdated')}</p>
        <p className="version">{t('privacy.version')}</p>

        <div className="policy-intro">
          <p>{t('privacy.intro1')}</p>
          <p>{t('privacy.intro2')}</p>
        </div>

        {/* Section 1: Controller Information */}
        <section className="policy-section">
          <h2>{t('privacy.s1.title')}</h2>
          <div className="info-box">
            <p><strong>{t('privacy.s1.companyLabel')}</strong> Кактус Соушъл ЕООД (Cactus Social Ltd.)</p>
            <p><strong>{t('privacy.s1.uicLabel')}</strong> 208807738</p>
            <p><strong>{t('privacy.s1.addressLabel')}</strong> ул. Шипченски проход № 18, бл. А, ет. 3, р-н Слатина, гр. София 1113, България</p>
            <p><strong>{t('privacy.s1.emailLabel')}</strong> {EMAIL_PRIVACY}</p>
            <p><strong>{t('privacy.s1.gdprContactLabel')}</strong> {EMAIL_GDPR}</p>
            <p><strong>{t('privacy.s1.dpoLabel')}</strong> {t('privacy.s1.dpoValue')}</p>
          </div>
          <p>{t('privacy.s1.description')}</p>
        </section>

        {/* Section 2: What Data We Collect */}
        <section className="policy-section">
          <h2>{t('privacy.s2.title')}</h2>

          <h3>{t('privacy.s2.s2_1.title')}</h3>
          <ul>
            <li><Trans i18nKey="privacy.s2.s2_1.accountInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_1.profileInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_1.eventInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_1.hashtagInfo" components={{ strong: <strong />, em: <em /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_1.badgesInfo" components={{ strong: <strong /> }} /></li>
            <li><strong>{t('privacy.s2.s2_1.photosLabel')}</strong>
              <ul>
                <li><Trans i18nKey="privacy.s2.s2_1.photoCover" components={{ strong: <strong /> }} /></li>
                <li><Trans i18nKey="privacy.s2.s2_1.photoOrganizer" components={{ strong: <strong /> }} /></li>
                <li><Trans i18nKey="privacy.s2.s2_1.photoParticipant" components={{ strong: <strong /> }} /></li>
              </ul>
            </li>
            <li><Trans i18nKey="privacy.s2.s2_1.profilePhotoInfo" components={{ strong: <strong /> }} /></li>
          </ul>

          <h3>{t('privacy.s2.s2_2.title')}</h3>
          <ul>
            <li><Trans i18nKey="privacy.s2.s2_2.locationInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_2.geoInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_2.tokenInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_2.votesInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_2.followingInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_2.friendsInfo" components={{ strong: <strong /> }} /></li>
          </ul>

          <h3>{t('privacy.s2.s2_3.title')}</h3>
          <ul>
            <li><Trans i18nKey="privacy.s2.s2_3.badgesReceived" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_3.reactionsInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s2.s2_3.offendingInfo" components={{ strong: <strong /> }} /></li>
          </ul>
        </section>

        {/* Section 3: Legal Basis for Processing */}
        <section className="policy-section">
          <h2>{t('privacy.s3.title')}</h2>
          <p>{t('privacy.s3.intro')}</p>

          <table className="legal-basis-table">
            <thead>
              <tr>
                <th>{t('privacy.s3.colData')}</th>
                <th>{t('privacy.s3.colBasis')}</th>
                <th>{t('privacy.s3.colExplanation')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('privacy.s3.rowAccountProfile')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.accountProfile')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowEventParticipation')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.eventParticipation')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowCoverImages')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.coverImages')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowOrganizerPhotos')}</td>
                <td>{t('privacy.s3.basisConsent')}</td>
                <td>{t('privacy.s3.organizerPhotos')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowParticipantPhotos')}</td>
                <td>{t('privacy.s3.basisConsent')}</td>
                <td>{t('privacy.s3.participantPhotos')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowGeolocation')}</td>
                <td>{t('privacy.s3.basisConsent')}</td>
                <td>{t('privacy.s3.geolocation')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowVotesFollowing')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.votesFollowing')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowFriendConnections')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.friendConnections')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowHashtags')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.hashtags')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowBadgesSent')}</td>
                <td>{t('privacy.s3.basisContract')}</td>
                <td>{t('privacy.s3.badgesSent')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s3.rowBadgeReactions')}</td>
                <td>{t('privacy.s3.basisLegitimate')}</td>
                <td>{t('privacy.s3.badgeReactions')}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Section 4: How We Use Your Data */}
        <section className="policy-section">
          <h2>{t('privacy.s4.title')}</h2>

          <h3>{t('privacy.s4.primaryTitle')}</h3>
          <ul>
            <li>✓ {t('privacy.s4.primary1')}</li>
            <li>✓ {t('privacy.s4.primary2')}</li>
            <li>✓ {t('privacy.s4.primary3')}</li>
            <li>✓ {t('privacy.s4.primary4')}</li>
          </ul>

          <h3>{t('privacy.s4.secondaryTitle')}</h3>
          <ul>
            <li>✓ {t('privacy.s4.secondary1')}</li>
            <li>✓ {t('privacy.s4.secondary2')}</li>
            <li>✓ {t('privacy.s4.secondary3')}</li>
            <li>✓ {t('privacy.s4.secondary4')}</li>
          </ul>

          <h3>{t('privacy.s4.notTitle')}</h3>
          <ul>
            <li>✗ {t('privacy.s4.not1')}</li>
            <li>✗ {t('privacy.s4.not2')}</li>
            <li>✗ {t('privacy.s4.not3')}</li>
            <li>✗ {t('privacy.s4.not4')}</li>
          </ul>
        </section>

        {/* Section 5: Photo-Specific Rights */}
        <section className="policy-section">
          <h2>{t('privacy.s5.title')}</h2>

          <div className="info-box">
            <h3>{t('privacy.s5.coverTitle')}</h3>
            <p>{t('privacy.s5.coverText')}</p>
          </div>

          <div className="info-box">
            <h3>{t('privacy.s5.organizerTitle')}</h3>
            <p>{t('privacy.s5.organizerText')}</p>
          </div>

          <div className="info-box">
            <h3>{t('privacy.s5.participantTitle')}</h3>
            <Trans i18nKey="privacy.s5.participantP1" components={{ strong: <strong /> }} parent="p" />
            <p>{t('privacy.s5.participantForEach')}</p>
            <ul>
              <li><Trans i18nKey="privacy.s5.participantApprove" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="privacy.s5.participantReject" components={{ strong: <strong /> }} /></li>
            </ul>
            <p>{t('privacy.s5.participantConsent')}</p>
          </div>

          <div className="info-box">
            <h3>{t('privacy.s5.appearTitle')}</h3>
            <p>{t('privacy.s5.appearText')}</p>
            <ul>
              <li><Trans i18nKey="privacy.s5.appearRemove" components={{ strong: <strong /> }} values={{ email: EMAIL_GDPR }} /></li>
              <li><Trans i18nKey="privacy.s5.appearFast" components={{ strong: <strong /> }} /></li>
            </ul>
            <p>{t('privacy.s5.appearNoAccount')}</p>
          </div>
        </section>

        {/* Section 6: Who We Share Data With */}
        <section className="policy-section">
          <h2>{t('privacy.s6.title')}</h2>

          <h3>{t('privacy.s6.otherUsersTitle')}</h3>
          <p>{t('privacy.s6.otherUsersIntro')}</p>
          <ul>
            <li><strong>{t('privacy.s6.otherUsersUsername')}</strong></li>
            <li><strong>{t('privacy.s6.otherUsersAvatar')}</strong></li>
            <li><strong>{t('privacy.s6.otherUsersInterests')}</strong></li>
            <li><strong>{t('privacy.s6.otherUsersGallery')}</strong></li>
            <li><strong>{t('privacy.s6.otherUsersBadges')}</strong></li>
            <li><Trans i18nKey="privacy.s6.otherUsersEmail" components={{ strong: <strong /> }} /></li>
          </ul>

          <h3>{t('privacy.s6.providersTitle')}</h3>
          <p>{t('privacy.s6.providersIntro')}</p>
          <ul>
            <li><Trans i18nKey="privacy.s6.providersHosting" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s6.providersDB" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s6.providersStorage" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s6.providersCDN" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s6.providersEmail" components={{ strong: <strong /> }} /></li>
          </ul>
          <p>{t('privacy.s6.providersDPA')}</p>

          <h3>{t('privacy.s6.legalTitle')}</h3>
          <p>{t('privacy.s6.legalIntro')}</p>
          <ul>
            <li>{t('privacy.s6.legal1')}</li>
            <li>{t('privacy.s6.legal2')}</li>
            <li>{t('privacy.s6.legal3')}</li>
            <li>{t('privacy.s6.legal4')}</li>
          </ul>

          <h3>{t('privacy.s6.notShareTitle')}</h3>
          <ul>
            <li>✗ {t('privacy.s6.notShare1')}</li>
            <li>✗ {t('privacy.s6.notShare2')}</li>
            <li>✗ {t('privacy.s6.notShare3')}</li>
          </ul>
        </section>

        {/* Section 7: International Transfers */}
        <section className="policy-section">
          <h2>{t('privacy.s7.title')}</h2>
          <p>{t('privacy.s7.p1')}</p>
          <p>{t('privacy.s7.p2')}</p>
          <ul>
            <li>{t('privacy.s7.item1')}</li>
            <li>{t('privacy.s7.item2')}</li>
            <li>{t('privacy.s7.item3')}</li>
          </ul>
          <p><strong>{t('privacy.s7.specificTitle')}</strong></p>
          <ul>
            <li>{t('privacy.s7.specific1')}</li>
            <li>{t('privacy.s7.specific2')}</li>
          </ul>
        </section>

        {/* Section 8: Authentication & Tracking */}
        <section className="policy-section">
          <h2>{t('privacy.s8.title')}</h2>

          <div className="info-box">
            <Trans i18nKey="privacy.s8.noCookies" components={{ strong: <strong /> }} parent="p" />
          </div>

          <h3>{t('privacy.s8.localStorageTitle')}</h3>
          <ul>
            <li><Trans i18nKey="privacy.s8.jwtInfo" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s8.langInfo" components={{ strong: <strong /> }} /></li>
          </ul>

          <h3>{t('privacy.s8.notTitle')}</h3>
          <ul>
            <li>✗ {t('privacy.s8.not1')}</li>
            <li>✗ {t('privacy.s8.not2')}</li>
            <li>✗ {t('privacy.s8.not3')}</li>
            <li>✗ {t('privacy.s8.not4')}</li>
          </ul>
        </section>

        {/* Section 9: Data Retention */}
        <section className="policy-section">
          <h2>{t('privacy.s9.title')}</h2>

          <table className="retention-table">
            <thead>
              <tr>
                <th>{t('privacy.s9.colType')}</th>
                <th>{t('privacy.s9.colPeriod')}</th>
                <th>{t('privacy.s9.colReason')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('privacy.s9.row1Type')}</td>
                <td>{t('privacy.s9.row1Period')}</td>
                <td>{t('privacy.s9.row1Reason')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s9.row2Type')}</td>
                <td>{t('privacy.s9.row2Period')}</td>
                <td>{t('privacy.s9.row2Reason')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s9.row3Type')}</td>
                <td>{t('privacy.s9.row3Period')}</td>
                <td>{t('privacy.s9.row3Reason')}</td>
              </tr>
              <tr>
                <td>{t('privacy.s9.row4Type')}</td>
                <td>{t('privacy.s9.row4Period')}</td>
                <td>{t('privacy.s9.row4Reason')}</td>
              </tr>
            </tbody>
          </table>

          <Trans i18nKey="privacy.s9.deletedNote" components={{ strong: <strong /> }} parent="p" />
        </section>

        {/* Section 10: Your Rights (Most Important Section) */}
        <section className="policy-section highlighted-section">
          <h2>{t('privacy.s10.title')}</h2>
          <p className="rights-intro">{t('privacy.s10.intro')}</p>

          <div className="rights-grid">
            <div className="right-card">
              <h3>{t('privacy.s10.accessTitle')}</h3>
              <p>{t('privacy.s10.accessText')}</p>
              <button className="action-button" onClick={handleExportMyData} disabled={exportLoading || !currentUser}>
                {exportLoading ? t('privacy.s10.exportingBtn') : t('privacy.s10.exportBtn')}
              </button>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.rectificationTitle')}</h3>
              <p>{t('privacy.s10.rectificationText')}</p>
              <button className="action-button">{t('privacy.s10.editProfileBtn')}</button>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.erasureTitle')}</h3>
              <p>{t('privacy.s10.erasureText')}</p>
              <button className="action-button" onClick={() => currentUser && setShowDeleteModal(true)} disabled={!currentUser}>
                {t('privacy.s10.deleteBtn')}
              </button>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.restrictTitle')}</h3>
              <p>{t('privacy.s10.restrictText')}</p>
              <a href={`mailto:${EMAIL_GDPR}`}>{t('privacy.s10.contactUs')}</a>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.portabilityTitle')}</h3>
              <p>{t('privacy.s10.portabilityText')}</p>
              <button className="action-button" onClick={handleDownloadJSON} disabled={exportLoading || !currentUser}>
                {exportLoading ? t('privacy.s10.exportingBtn') : t('privacy.s10.downloadBtn')}
              </button>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.objectTitle')}</h3>
              <p>{t('privacy.s10.objectText')}</p>
              <a href={`mailto:${EMAIL_GDPR}`}>{t('privacy.s10.objectLink')}</a>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.automatedTitle')}</h3>
              <p>{t('privacy.s10.automatedText')}</p>
            </div>

            <div className="right-card">
              <h3>{t('privacy.s10.withdrawTitle')}</h3>
              <p>{t('privacy.s10.withdrawText')}</p>
              <a href={`mailto:${EMAIL_GDPR}`}>{t('privacy.s10.withdrawLink')}</a>
            </div>
          </div>

          <div className="how-to-exercise">
            <h3>{t('privacy.s10.howToTitle')}</h3>
            <ol>
              <li><Trans i18nKey="privacy.s10.howTo1" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="privacy.s10.howTo2" components={{ strong: <strong /> }} values={{ email: EMAIL_GDPR }} /></li>
              <li><Trans i18nKey="privacy.s10.howTo3" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="privacy.s10.howTo4" components={{ strong: <strong /> }} /></li>
              <li><Trans i18nKey="privacy.s10.howTo5" components={{ strong: <strong /> }} /></li>
            </ol>
          </div>
        </section>

        {/* Section 11: Data Security */}
        <section className="policy-section">
          <h2>{t('privacy.s11.title')}</h2>
          <p>{t('privacy.s11.intro')}</p>

          <h3>{t('privacy.s11.technicalTitle')}</h3>
          <ul>
            <li><Trans i18nKey="privacy.s11.tech1" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s11.tech2" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s11.tech3" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s11.tech4" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s11.tech5" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="privacy.s11.tech6" components={{ strong: <strong /> }} /></li>
          </ul>

          <h3>{t('privacy.s11.orgTitle')}</h3>
          <ul>
            <li>{t('privacy.s11.org1')}</li>
            <li>{t('privacy.s11.org2')}</li>
            <li>{t('privacy.s11.org3')}</li>
            <li>{t('privacy.s11.org4')}</li>
          </ul>

          <h3>{t('privacy.s11.breachTitle')}</h3>
          <p>{t('privacy.s11.breachIntro')}</p>
          <ul>
            <li>{t('privacy.s11.breach1')}</li>
            <li>{t('privacy.s11.breach2')}</li>
            <li>{t('privacy.s11.breach3')}</li>
          </ul>
        </section>

        {/* Section 12: Children's Privacy */}
        <section className="policy-section">
          <h2>{t('privacy.s12.title')}</h2>
          <div className="warning-box">
            <Trans i18nKey="privacy.s12.ageReq" components={{ strong: <strong /> }} parent="p" />
            <p>{t('privacy.s12.noKids', { email: EMAIL_PRIVACY })}</p>
            <p>{t('privacy.s12.deleteKids')}</p>
          </div>
        </section>

        {/* Section 13: Changes to Policy */}
        <section className="policy-section">
          <h2>{t('privacy.s13.title')}</h2>
          <p>{t('privacy.s13.intro')}</p>
          <ul>
            <li>{t('privacy.s13.item1')}</li>
            <li>{t('privacy.s13.item2')}</li>
            <li>{t('privacy.s13.item3')}</li>
            <li>{t('privacy.s13.item4')}</li>
          </ul>
          <p>{t('privacy.s13.previous')}</p>
        </section>

        {/* Section 14: Supervisory Authority */}
        <section className="policy-section">
          <h2>{t('privacy.s14.title')}</h2>
          <p>{t('privacy.s14.p1')}</p>
          <p>
            <strong>{t('privacy.s14.euAuthorities')}</strong>{' '}
            <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">
              edpb.europa.eu
            </a>
          </p>
          <p>
            <strong>{t('privacy.s14.bgAuthorityTitle')}</strong><br />
            Комисия за защита на личните данни (Commission for Personal Data Protection)<br />
            бул. „Проф. Цветан Лазаров" № 2, гр. София 1592, България<br />
            {t('privacy.s14.bgPhone')}<br />
            Website: <a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer">cpdp.bg</a>
          </p>
          <p>{t('privacy.s14.encourage', { email: EMAIL_GDPR })}</p>
        </section>

        {/* Section 15: Contact Us */}
        <section className="policy-section">
          <h2>{t('privacy.s15.title')}</h2>
          <div className="contact-info">
            <p><strong>{t('privacy.s15.generalLabel')}</strong></p>
            <p>Email: {EMAIL_PRIVACY}</p>

            <p><strong>{t('privacy.s15.gdprLabel')}</strong></p>
            <p>Email: {EMAIL_GDPR}</p>

            <p><strong>{t('privacy.s15.photoLabel')}</strong></p>
            <p>Email: {EMAIL_GDPR} {t('privacy.s15.photoHelper')}</p>

            <p><strong>{t('privacy.s15.dpoLabel')}</strong></p>
            <p>{t('privacy.s15.dpoValue')}</p>

            <p><strong>{t('privacy.s15.addressLabel')}</strong></p>
            <p>ул. Шипченски проход № 18, бл. А, ет. 3, р-н Слатина, гр. София 1113, България</p>

            <p><strong>{t('privacy.s15.responseLabel')}</strong></p>
            <p>{t('privacy.s15.responseText')}</p>
          </div>
        </section>

        {/* Quick Links */}
        <section className="policy-section quick-actions">
          <h2>{t('privacy.quickActions.title')}</h2>
          <div className="action-buttons-row">
            <button className="primary-action-btn" onClick={handleExportMyData} disabled={exportLoading || !currentUser}>
              {exportLoading ? t('privacy.quickActions.exportingBtn') : t('privacy.quickActions.exportBtn')}
            </button>
            <button className="danger-action-btn" onClick={() => currentUser && setShowDeleteModal(true)} disabled={!currentUser}>
              {t('privacy.quickActions.deleteBtn')}
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="policy-footer">
          <p>{t('privacy.footer')}</p>
          <p>
            <a href="/terms">{t('common.termsOfUse')}</a> |
            <a href="/legal">{t('common.legalNotice')}</a> |
            <a href={`mailto:${EMAIL_PRIVACY}`}>{t('privacy.s15.generalLabel')}</a>
          </p>
        </footer>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !deletingAccount && setShowDeleteModal(false)}>
          <div className="modal-content delete-account-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('privacy.deleteModal.title')}</h3>
            <p>{t('privacy.deleteModal.confirm')}</p>
            <ul className="delete-account-list">
              <li>{t('privacy.deleteModal.item1')}</li>
              <li>{t('privacy.deleteModal.item2')}</li>
              <li>{t('privacy.deleteModal.item3')}</li>
              <li>{t('privacy.deleteModal.item4')}</li>
            </ul>
            <p className="delete-account-warning">{t('privacy.deleteModal.warning')}</p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)} disabled={deletingAccount}>
                {t('privacy.deleteModal.cancelBtn')}
              </button>
              <button className="confirm-delete-btn" onClick={handleDeleteAccount} disabled={deletingAccount}>
                {deletingAccount ? t('privacy.deleteModal.deletingBtn') : t('privacy.deleteModal.confirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrivacyPolicy;
